import { useState } from "react";
import { colors } from "../../shared/colors";
import type { ConnectorWithOAuth } from "../../shared/connectors/getTargetValue";
import { isDraft } from "../../shared/connectors/isDraft";
import { connectorDirectory } from "../lib/connectorDirectory";
import { resolveProduct } from "../lib/connector";
import { faviconPath } from "../lib/apiPaths";
import { apiUrl } from "../lib/tenantOrigin";
import {
  domainFromUrlPattern,
  isIpAddress,
  rootDomain,
} from "../../shared/connectors/urlDomain";

type Props = { size: number; faded?: boolean } & (
  | { app: ConnectorWithOAuth; template?: never }
  | { template: { name: string; identifier: string }; app?: never }
);

const hashCode = (text: string) =>
  [...text].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);

const hashColor = (text: string) => colors[hashCode(text) % colors.length];

const hashRotation = (text: string) => (hashCode(text) % 2 === 0 ? 35 : -35);

export function ConnectorLogo(props: Props) {
  const { size } = props;
  const { name, identifier } = props.app ?? props.template;
  const resolved = resolveProduct(props.app ?? props.template);
  const brandIdentifier =
    resolved && connectorDirectory.some((a) => a.identifier === resolved)
      ? resolved
      : identifier;
  const sizeStyle = { width: size, height: size, borderRadius: size / 4 };
  const draft = props.app ? isDraft(props.app) : false;
  const draftStyle =
    draft || props.faded
      ? { filter: "grayscale(100%)", opacity: draft ? 0.5 : 0.4 }
      : {};
  const firstLetter = name.trim()[0];

  // A real API key connector points at an arbitrary domain, so show that
  // domain's favicon/letter rather than the generic key; the picker template has
  // no domain, so let it fall through to the apiKey brand logo.
  const useLetterVariant =
    (brandIdentifier === "apiKey" && !!props.app) ||
    !connectorDirectory.some((a) => a.identifier === brandIdentifier);

  if (useLetterVariant && firstLetter) {
    return (
      <LetterOrFavicon
        size={size}
        sizeStyle={sizeStyle}
        draftStyle={draftStyle}
        name={name}
        urlPattern={props.app?.urlPattern}
        firstLetter={firstLetter}
      />
    );
  }

  const appConfig = connectorDirectory.find(
    (a) => a.identifier === brandIdentifier
  );
  if (!appConfig) return null;

  return (
    <img
      alt={`${name} logo`}
      style={{ ...sizeStyle, ...draftStyle, flexShrink: 0 }}
      src={appConfig.definition.logo}
    />
  );
}

const hostnameRegex =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

// Favicon services only know crawled public domains, so IPs and single-label
// hosts (localhost, bare intranet names) can never resolve — skip them.
const canHaveFavicon = (host: string) =>
  host.includes(".") && !isIpAddress(host);

export function faviconDomain(
  name: string,
  urlPattern: string | undefined
): string | null {
  const domain = urlPattern ? domainFromUrlPattern(urlPattern) : null;
  if (domain) return canHaveFavicon(domain) ? domain : null;
  const trimmed = name.trim();
  if (hostnameRegex.test(trimmed) && canHaveFavicon(trimmed))
    return rootDomain(trimmed);
  return null;
}

function faviconUrl(
  name: string,
  urlPattern: string | undefined,
  externalApiUrl: string
): string | null {
  const domain = faviconDomain(name, urlPattern);
  if (!domain) return null;
  return `${externalApiUrl}${faviconPath(domain)}`;
}

function LetterOrFavicon({
  size,
  sizeStyle,
  draftStyle,
  name,
  urlPattern,
  firstLetter,
}: {
  size: number;
  sizeStyle: React.CSSProperties;
  draftStyle: React.CSSProperties;
  name: string;
  urlPattern: string | undefined;
  firstLetter: string;
}) {
  const externalApiUrl = apiUrl();
  const favicon = faviconUrl(name, urlPattern, externalApiUrl);
  const [faviconFailed, setFaviconFailed] = useState(false);

  if (favicon && !faviconFailed) {
    return (
      <img
        alt={`${name} logo`}
        style={{
          ...sizeStyle,
          ...draftStyle,
          flexShrink: 0,
          objectFit: "contain",
        }}
        src={favicon}
        onError={() => setFaviconFailed(true)}
      />
    );
  }

  const color = hashColor(name);
  const rotation = hashRotation(name);
  const letter = firstLetter.toUpperCase();

  const rows = 7;
  const cols = 5;
  const letters = Array.from({ length: rows * cols }, () => letter);

  return (
    <div
      style={{
        ...sizeStyle,
        ...draftStyle,
        background: `var(--${color}-600)`,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: 1,
          fontFamily: "Inter",
          fontStyle: "normal",
          fontWeight: 700,
          fontSize: size > 20 ? 8 : 6,
          lineHeight: `${size > 20 ? 8 : 6}px`,
          color: `var(--${color}-1000)`,
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {letters.map((l, i) => (
          // oxlint-disable-next-line react/no-array-index-key -- static array of identical letters
          <span key={i}>{l}</span>
        ))}
      </div>
    </div>
  );
}
